import { createMemo, createSignal, onCleanup, onMount } from 'solid-js';
import { STORAGE_KEY_AUDIO_PREFS, FALLBACK_HEADER_HEIGHT } from '../../game/constants';
import { gameStore } from '../../store/gameStore';
import StatsDrawerToggle from './StatsDrawerToggle';

function useAudioPrefs() {
  const read = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_AUDIO_PREFS);
      const json = raw ? JSON.parse(raw) : {};
      return {
        musicEnabled: json.musicEnabled !== false,
        effectsEnabled: json.effectsEnabled !== false,
      };
    } catch {
      return { musicEnabled: true, effectsEnabled: true };
    }
  };
  const [musicEnabled, setMusicEnabled] = createSignal(read().musicEnabled);
  const [effectsEnabled, setEffectsEnabled] = createSignal(read().effectsEnabled);
  const [ready, setReady] = createSignal<boolean>(false);

  const syncFromStorage = () => {
      const v = read();
      setMusicEnabled(v.musicEnabled);
      setEffectsEnabled(v.effectsEnabled);
    };
  onMount(() => {
    const markReady = () => {
      setReady(true);
      syncFromStorage();
      try { (window as any).memePumpApp?.updateAudioButtons?.(); } catch {}
    };
    if ((window as any).memePumpApp) markReady();
    const onReady = () => markReady();
    window.addEventListener('memePump:ready', onReady, { once: true });
    onCleanup(() => window.removeEventListener('memePump:ready', onReady));
  });

  const toggleMusic = () => {
    if (!ready()) return;
    const next = !musicEnabled();
    setMusicEnabled(next);
    const app = (window as any).memePumpApp;
    if (app && app.persistAudioPrefs && app.audio && app.updateAudioButtons) {
      try {
        app.persistAudioPrefs({ musicEnabled: next });
        app.audio.setPreferences({ musicEnabled: next });
        if (next) { app.audio.startBackground(); app.audio.play('ui-toggle', { allowRestart: true }); }
        else { app.audio.stopBackground(); }
        app.updateAudioButtons();
      } catch {}
    } else if ((window as any).memePumpToggleMusic) {
      try { (window as any).memePumpToggleMusic(); } catch {}
    }
  };
  const toggleSfx = () => {
    if (!ready()) return;
    const next = !effectsEnabled();
    setEffectsEnabled(next);
    const app = (window as any).memePumpApp;
    if (app && app.persistAudioPrefs && app.audio && app.updateAudioButtons) {
      try {
        app.persistAudioPrefs({ effectsEnabled: next });
        app.audio.setPreferences({ effectsEnabled: next });
        app.audio.play('ui-toggle', { allowRestart: true });
        app.updateAudioButtons();
      } catch {}
    } else if ((window as any).memePumpToggleSfx) {
      try { (window as any).memePumpToggleSfx(); } catch {}
    }
  };
  return { musicEnabled, effectsEnabled, toggleMusic, toggleSfx, ready };
}

export default function Header() {
  const { musicEnabled, effectsEnabled, toggleMusic, toggleSfx, ready } = useAudioPrefs();

  const [menuOpen, setMenuOpen] = createSignal(false);
  const demoBadgeText = createMemo(() => gameStore.demoLabel() || 'DEMO');
  const closeMenu = () => setMenuOpen(false);
  const toggleMenu = () => setMenuOpen(v => !v);

  const onDocClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const menu = document.querySelector('.header-menu');
    const btn = document.getElementById('header-menu-btn');
    if (!menu || !btn) return;
    if (menu.contains(target) || btn.contains(target)) return;
    if (menuOpen()) closeMenu();
  };
  onMount(() => { document.addEventListener('click', onDocClick); });
  onCleanup(() => document.removeEventListener('click', onDocClick));

  const openModal = (id: string) => {
    if (!ready()) return;
    (window as any).openModal?.(id);
    closeMenu();
  };

  const toggleStatsDrawer = () => {
    const event = new CustomEvent('memePump:toggleStatsDrawer', { detail: { open: undefined } });
    window.dispatchEvent(event);
    try { (window as any).memePumpApp?.audio?.play('ui-toggle', { allowRestart: true }); } catch {}
    closeMenu();
  };

  onMount(() => {
    const updateHeaderH = () => {
      try {
        const hdr = document.querySelector('.game-header') as HTMLElement | null;
        const h = hdr ? hdr.getBoundingClientRect().height : FALLBACK_HEADER_HEIGHT;
        document.documentElement.style.setProperty('--mp-header-h', `${Math.round(h)}px`);
      } catch {}
    };
    updateHeaderH();
    const ro = new ResizeObserver(() => updateHeaderH());
    const hdr = document.querySelector('.game-header') as HTMLElement | null;
    if (hdr) ro.observe(hdr);
    window.addEventListener('resize', updateHeaderH);
    onCleanup(() => { try { ro.disconnect(); } catch {}; window.removeEventListener('resize', updateHeaderH); });
  });

  return (
    <>
      <div class="header-status-stack" aria-live="polite">
        <div
          class="header-status-chip demo"
          title="Demo mode"
        >
          <span class="chip-icon" aria-hidden="true">◈</span>
          <span class="chip-text">{demoBadgeText()}</span>
        </div>
      </div>

      <button
        id="header-menu-btn"
        class="button-secondary icon-btn show-on-mobile"
        aria-label="Menu"
        title="Menu"
        onClick={toggleMenu}
        aria-expanded={menuOpen() ? 'true' : 'false'}
        aria-controls="header-menu-panel"
      >
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="5" cy="6" r="1.2" fill="currentColor" />
            <circle cx="5" cy="12" r="1.2" fill="currentColor" />
            <circle cx="5" cy="18" r="1.2" fill="currentColor" />
            <path d="M9 6h10" />
            <path d="M9 12h10" />
            <path d="M9 18h10" />
          </svg>
        </span>
      </button>

      <button
        id="music-toggle-btn"
        class="button-secondary icon-btn audio-btn hide-on-mobile"
        classList={{ 'is-off': !musicEnabled() }}
        aria-label={`Music: ${musicEnabled() ? 'On' : 'Off'}`}
        title={musicEnabled() ? 'Music On' : 'Music Off'}
        aria-pressed={musicEnabled() ? 'true' : 'false'}
        disabled={!ready()}
        onClick={toggleMusic}
      >
        <span class="icon icon-music" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 4v11" />
            <path d="M12 5l7-2v9" />
            <circle cx="8" cy="18" r="2.6" />
            <circle cx="17" cy="14.5" r="2.2" />
          </svg>
        </span>
      </button>

      <button
        id="sfx-toggle-btn"
        class="button-secondary icon-btn audio-btn hide-on-mobile"
        classList={{ 'is-off': !effectsEnabled() }}
        aria-label={`Sound: ${effectsEnabled() ? 'On' : 'Off'}`}
        title={effectsEnabled() ? 'Sound On' : 'Sound Off'}
        aria-pressed={effectsEnabled() ? 'true' : 'false'}
        disabled={!ready()}
        onClick={toggleSfx}
      >
        <span class="icon icon-sfx" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M4.5 10.5v3h3l4.5 3.5V7l-4.5 3.5z" />
            <path data-wave d="M16.5 10a3.5 3.5 0 0 1 0 4" />
            <path data-wave d="M18.5 8a6 6 0 0 1 0 8" />
          </svg>
        </span>
      </button>

      <span class="hide-on-mobile"><StatsDrawerToggle /></span>

      <button id="profile-btn" class="button-secondary icon-btn hide-on-mobile" aria-label="Open profile settings" title="Profile" disabled={!ready()} onClick={[openModal, 'profile']}>
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="9" r="3.6" />
            <path d="M4 20c1.8-3.6 5-5.2 8-5.2s6.2 1.6 8 5.2" />
          </svg>
        </span>
      </button>

      <button id="how-to-btn" class="button-secondary icon-btn hide-on-mobile" aria-label="How to play" title="How to play" disabled={!ready()} onClick={[openModal, 'how-to']}>
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 10v5" />
            <circle cx="12" cy="7.2" r="1.2" fill="currentColor" />
          </svg>
        </span>
      </button>

      <button id="provably-fair-btn" class="button-secondary icon-btn hide-on-mobile" aria-label="Provably fair" title="Provably fair" disabled={!ready()} onClick={[openModal, 'provably-fair']}>
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3 19 6v5c0 4.5-2.8 7.7-7 9-4.2-1.3-7-4.5-7-9V6l7-3Z" />
            <path d="m9.2 12.2 2.1 2.1 3.8-4.2" />
          </svg>
        </span>
      </button>

      <div id="header-menu-panel" class="header-menu" data-open={menuOpen() ? 'true' : 'false'}>
        <div class="menu-head">
          <div class="menu-title">Quick Panel</div>
          <div class="menu-sub">Stats, guides, audio</div>
        </div>
        <div class="menu-grid">
          <button class="menu-tile" disabled={!ready()} onClick={toggleStatsDrawer} aria-label="Stats">
            <span class="menu-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 19h16" />
                <path d="M7 16v-5" />
                <path d="M12 16v-8" />
                <path d="M17 16v-11" />
                <path d="M6 11l4-4 3 3 5-6" />
              </svg>
            </span>
            <span class="menu-text">
              <span class="menu-label">Stats</span>
              <span class="menu-meta">Round + history</span>
            </span>
          </button>
          <button class="menu-tile" disabled={!ready()} onClick={[openModal, 'profile']} aria-label="Profile">
            <span class="menu-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="9" r="3.6" />
                <path d="M4 20c1.8-3.6 5-5.2 8-5.2s6.2 1.6 8 5.2" />
              </svg>
            </span>
            <span class="menu-text">
              <span class="menu-label">Profile</span>
              <span class="menu-meta">Identity + prefs</span>
            </span>
          </button>
          <button class="menu-tile" disabled={!ready()} onClick={[openModal, 'how-to']} aria-label="How to play">
            <span class="menu-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 10v5" />
                <circle cx="12" cy="7.2" r="1.2" fill="currentColor" />
              </svg>
            </span>
            <span class="menu-text">
              <span class="menu-label">How to Play</span>
              <span class="menu-meta">Fast guide</span>
            </span>
          </button>
          <button class="menu-tile" disabled={!ready()} onClick={[openModal, 'provably-fair']} aria-label="Provably fair">
            <span class="menu-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 3 19 6v5c0 4.5-2.8 7.7-7 9-4.2-1.3-7-4.5-7-9V6l7-3Z" />
                <path d="m9.2 12.2 2.1 2.1 3.8-4.2" />
              </svg>
            </span>
            <span class="menu-text">
              <span class="menu-label">Provably Fair</span>
              <span class="menu-meta">Verify rounds</span>
            </span>
          </button>
        </div>
        <div class="menu-divider" aria-hidden="true" />
        <div class="menu-grid menu-grid-tight">
          <button
            class="menu-tile menu-toggle"
            classList={{ 'is-off': !musicEnabled() }}
            disabled={!ready()}
            onClick={toggleMusic}
            aria-label={`Music: ${musicEnabled() ? 'On' : 'Off'}`}
            aria-pressed={musicEnabled() ? 'true' : 'false'}
          >
            <span class="menu-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 4v11" />
                <path d="M12 5l7-2v9" />
                <circle cx="8" cy="18" r="2.6" />
                <circle cx="17" cy="14.5" r="2.2" />
              </svg>
            </span>
            <span class="menu-text">
              <span class="menu-label">Music</span>
              <span class="menu-pill">{musicEnabled() ? 'On' : 'Off'}</span>
            </span>
          </button>
          <button
            class="menu-tile menu-toggle"
            classList={{ 'is-off': !effectsEnabled() }}
            disabled={!ready()}
            onClick={toggleSfx}
            aria-label={`Sound: ${effectsEnabled() ? 'On' : 'Off'}`}
            aria-pressed={effectsEnabled() ? 'true' : 'false'}
          >
            <span class="menu-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4.5 10.5v3h3l4.5 3.5V7l-4.5 3.5z" />
                <path data-wave d="M16.5 10a3.5 3.5 0 0 1 0 4" />
                <path data-wave d="M18.5 8a6 6 0 0 1 0 8" />
              </svg>
            </span>
            <span class="menu-text">
              <span class="menu-label">Sound</span>
              <span class="menu-pill">{effectsEnabled() ? 'On' : 'Off'}</span>
            </span>
          </button>
        </div>
      </div>
    </>
  );
}
